import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Team, Player } from '../types';
import { Layout } from '../components/Layout';
import { CreatePlayerModal } from '../components/CreatePlayerModal';
import { ImportPlayersModal } from '../components/ImportPlayersModal';
import { EditTeamModal } from '../components/EditTeamModal';
import { teamService } from '../services/teamService';
import { Card, Button, Grid, FlexContainer } from '../styles/GlobalStyles';
import { LoadingSpinner } from '../components/LoadingSpinner';

// PageHeader removed - using TeamHeader instead

const PageTitle = styled.h1`
  color: var(--text-primary);
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: var(--spacing-sm);
`;

const TeamInfo = styled.div`
  color: var(--text-secondary);
  font-size: 1rem;
  margin-bottom: var(--spacing-lg);
`;

const TeamHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-xl);
  
  @media (max-width: 767px) {
    flex-direction: column;
    gap: var(--spacing-md);
  }
`;

const TeamHeaderContent = styled.div`
  flex: 1;
`;

const EditableInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-xs);
`;

const EditButton = styled.button`
  background: none;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  padding: var(--spacing-xs) var(--spacing-sm);
  color: var(--text-secondary);
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    background: var(--background-secondary);
  }
`;

const SectionTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: var(--spacing-lg);
`;

const PlayerCard = styled(Card)`
  transition: transform 0.2s ease-in-out;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
`;

const PlayerName = styled.h3`
  color: var(--text-primary);
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: var(--spacing-sm);
`;

const PlayerInfo = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin-bottom: var(--spacing-md);
`;

const PlayerActions = styled(FlexContainer)`
  justify-content: flex-end;
  gap: var(--spacing-sm);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--spacing-2xl);
  color: var(--text-secondary);
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: var(--spacing-md);
  color: var(--text-primary);
`;

const ActionButtons = styled(FlexContainer)`
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-xl);
  
  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const ReadOnlyBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: var(--border-radius-sm);
  color: #92400e;
  font-size: 0.875rem;
  font-weight: 500;
  
  &::before {
    content: '👁️';
  }
`;

export const TeamPage: React.FC = () => {
    const { teamId } = useParams<{ teamId: string }>();
    const navigate = useNavigate();
    const [team, setTeam] = useState<Team | null>(null);
    const [players, setPlayers] = useState<Player[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [error, setError] = useState<string>('');

    // Check if user is a collaborating coach (read-only access)
    const isCollaboratingCoach = team?.userRole === 'COLLABORATING_COACH';
    const canEdit = !isCollaboratingCoach;

    useEffect(() => {
        if (teamId) {
            loadTeamData();
        }
    }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadTeamData = async () => {
        if (!teamId) return;

        try {
            setIsLoading(true);
            const [teamData, playersData] = await Promise.all([
                teamService.getTeam(teamId),
                teamService.getTeamPlayers(teamId),
            ]);
            console.log('TeamPage loaded players:', playersData.map(p => ({
                id: p.id,
                name: `${p.firstName} ${p.lastName}`,
                position: p.position
            })));
            setTeam(teamData);
            setPlayers(playersData);
        } catch (error: any) {
            setError(error.response?.data?.error?.message || 'Failed to load team data');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePlayerCreated = (newPlayer: Player) => {
        setPlayers([...players, newPlayer]);
        setShowCreateModal(false);
    };

    const handlePlayersImported = (importedPlayers: Player[]) => {
        setPlayers([...players, ...importedPlayers]);
        setShowImportModal(false);
    };

    const handleTeamUpdated = (updatedTeam: Team) => {
        setTeam(updatedTeam);
        setShowEditModal(false);
    };

    const handleTeamDeleted = () => {
        // Navigate back to dashboard when team is deleted
        navigate('/dashboard');
    };



    const formatDate = (date: Date | string) => {
        return new Date(date).toLocaleDateString();
    };

    const calculateAge = (dateOfBirth: Date | string) => {
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }

        return age;
    };

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (!team) {
        return (
            <Layout>
                <div>Team not found</div>
            </Layout>
        );
    }

    return (
        <Layout>
            <TeamHeader>
                <TeamHeaderContent>
                    <PageTitle>{team.name}</PageTitle>
                    <TeamInfo>
                        <div>Age Group: {team.ageGroup} | Season: {team.season}</div>
                        <div>Assessment Interval: {team.assessmentInterval} weeks</div>
                    </TeamInfo>
                    {isCollaboratingCoach && (
                        <ReadOnlyBadge>Read-Only Access (Collaborator)</ReadOnlyBadge>
                    )}
                    {canEdit && (
                        <EditableInfo>
                            <EditButton onClick={() => setShowEditModal(true)}>
                                ✏️ Edit Team Settings
                            </EditButton>
                        </EditableInfo>
                    )}
                </TeamHeaderContent>
            </TeamHeader>

            {error && (
                <Card>
                    <div style={{ color: 'var(--error-color)' }}>{error}</div>
                </Card>
            )}

            <FlexContainer $justify="space-between" $align="center">
                <SectionTitle>Players ({players.length})</SectionTitle>
                <ActionButtons>
                    <Button 
                        $variant="secondary" 
                        as={Link} 
                        to={`/reports/team/${teamId}`}
                    >
                        Team Analytics
                    </Button>
                    {canEdit && (
                        <>
                            <Button onClick={() => setShowCreateModal(true)}>
                                Add Player
                            </Button>
                            <Button $variant="secondary" onClick={() => setShowImportModal(true)}>
                                Import CSV
                            </Button>
                        </>
                    )}
                </ActionButtons>
            </FlexContainer>

            {players.length === 0 ? (
                <EmptyState>
                    <EmptyStateTitle>No players yet</EmptyStateTitle>
                    <p>
                        {isCollaboratingCoach 
                            ? 'This team has no players yet. Only the team owner can add players.'
                            : 'Add players to start tracking their development.'
                        }
                    </p>
                    {canEdit && (
                        <FlexContainer $justify="center" $gap="var(--spacing-md)" style={{ marginTop: 'var(--spacing-lg)' }}>
                            <Button onClick={() => setShowCreateModal(true)}>
                                Add First Player
                            </Button>
                            <Button $variant="secondary" onClick={() => setShowImportModal(true)}>
                                Import from CSV
                            </Button>
                        </FlexContainer>
                    )}
                </EmptyState>
            ) : (
                <Grid $columns={3}>
                    {players.map((player) => (
                        <PlayerCard key={player.id}>
                            <Link
                                to={`/team/${teamId}/player/${player.id}`}
                                style={{ textDecoration: 'none', color: 'inherit' }}
                            >
                                <PlayerName>{player.firstName} {player.lastName}</PlayerName>
                                <PlayerInfo>
                                    <div>Position: {player.position}</div>
                                    <div>Age: {calculateAge(player.dateOfBirth)}</div>
                                    <div>DOB: {formatDate(player.dateOfBirth)}</div>
                                    {player.fanNumber && <div>FAN: {player.fanNumber}</div>}
                                </PlayerInfo>
                            </Link>
                            {!isCollaboratingCoach && (
                                <PlayerActions>
                                    <Button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            window.location.href = `/team/${teamId}/assessment/${player.id}`;
                                        }}
                                    >
                                        Assess
                                    </Button>
                                </PlayerActions>
                            )}
                        </PlayerCard>
                    ))}
                </Grid>
            )}

            {showCreateModal && canEdit && (
                <CreatePlayerModal
                    teamId={teamId!}
                    onClose={() => setShowCreateModal(false)}
                    onPlayerCreated={handlePlayerCreated}
                />
            )}

            {showImportModal && canEdit && (
                <ImportPlayersModal
                    teamId={teamId!}
                    onClose={() => setShowImportModal(false)}
                    onPlayersImported={handlePlayersImported}
                />
            )}

            {showEditModal && team && (
                <EditTeamModal
                    team={team}
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onTeamUpdated={handleTeamUpdated}
                    onTeamDeleted={canEdit ? handleTeamDeleted : undefined}
                />
            )}
        </Layout>
    );
};